import { currencyDefinition, minorUnitsPerMajor } from '@optiflow/types';

/**
 * Money as integer minor units, converted only at the edges.
 *
 * Nothing here multiplies or divides a parsed float. `1.005 * 100` evaluates to 100.49999999999999
 * in IEEE 754, so a price typed by a user would silently round down by a piastre — and the same
 * class of error compounds across a basket. Every conversion below works on the decimal *string*,
 * which keeps the arithmetic exact for any number a person can type.
 *
 * The exponent comes from the currency, never from a constant: a Kuwaiti dinar has three decimal
 * places, so `× 100` would misprice it by a factor of ten.
 */

const AMOUNT_PATTERN = /^-?(\d+)(?:\.(\d+))?$/;

/**
 * Parses a typed major-unit amount into integer minor units, or null when unusable.
 *
 * The digit one place past the currency's precision decides a half-up rounding, the way an
 * accountant would: 1.005 in a 2-decimal currency becomes 101 minor units, not 100.
 */
export function parseMoneyToMinor(value: string, currencyCode: string): number | null {
  const trimmed = value.trim();
  if (trimmed === '') return null;

  const match = AMOUNT_PATTERN.exec(trimmed);
  if (!match) return null;

  const exponent = currencyDefinition(currencyCode).minorUnitExponent;
  const [, whole = '0', fraction = ''] = match;

  // One digit beyond the currency's precision is all a half-up decision needs.
  const padded = fraction.padEnd(exponent + 1, '0');
  const minorFraction = exponent === 0 ? 0 : Number(padded.slice(0, exponent));
  const roundUp = Number(padded[exponent] ?? '0') >= 5;

  const magnitude =
    Number(whole) * minorUnitsPerMajor(currencyCode) + minorFraction + (roundUp ? 1 : 0);
  if (!Number.isSafeInteger(magnitude)) return null;

  return trimmed.startsWith('-') ? -magnitude : magnitude;
}

/** Renders minor units as an editable major-unit string at the currency's own precision. */
export function minorToInput(minor: number | null | undefined, currencyCode: string): string {
  if (minor === null || minor === undefined) return '';

  const exponent = currencyDefinition(currencyCode).minorUnitExponent;
  return (minor / minorUnitsPerMajor(currencyCode)).toFixed(exponent);
}

/**
 * Formats minor units for display.
 *
 * `Intl` already knows each currency's precision, but it is pinned explicitly here so a display
 * can never disagree with the arithmetic that produced the number.
 */
export function formatMoneyMinor(
  minor: number | null | undefined,
  currencyCode: string,
  localeTag: string,
  options: { showSymbol?: boolean } = {},
): string {
  if (minor === null || minor === undefined) return '—';

  const { minorUnitExponent } = currencyDefinition(currencyCode);
  const major = minor / minorUnitsPerMajor(currencyCode);

  return new Intl.NumberFormat(localeTag, {
    ...(options.showSymbol === false ? {} : { style: 'currency' as const, currency: currencyCode }),
    minimumFractionDigits: minorUnitExponent,
    maximumFractionDigits: minorUnitExponent,
  }).format(major);
}
